import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import AdBanner from "@/components/AdBanner";
import { Disc3, MapPin, Clock, Layers, ArrowRight } from "lucide-react";

const HERO_BG =
  "https://customer-assets-agu9un31.emergentagent.net/job_poster-portfolio-3/artifacts/m89rqc8q_IMG_2027.jpeg";

const CATEGORIES = ["Concert", "Movie", "Theater", "Vintage", "Advertising", "Anime", "Military", "Car", "Eclectic", "Adult 18+"];

export default function Landing() {
  return (
    <div className="min-h-screen">
      <header className="relative z-20 flex items-center justify-between px-6 lg:px-12 py-5 border-b border-stroke bg-surface/70 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <Disc3 className="text-acid" size={28} />
          <span className="font-display text-3xl text-paper">POSTERVAULT</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/advertise" data-testid="landing-advertise-link" className="font-mono text-xs uppercase tracking-wider text-muted-gray hover:text-paper px-3 py-2 hidden sm:block">
            Advertise
          </Link>
          <Link to="/sponsorship" data-testid="landing-sponsorship-link" className="font-mono text-xs uppercase tracking-wider text-muted-gray hover:text-paper px-3 py-2 hidden sm:block">
            Sponsorship
          </Link>
          <Link to="/login" data-testid="landing-login-link" className="font-mono text-xs uppercase tracking-wider text-muted-gray hover:text-paper px-3 py-2">
            Log In
          </Link>
          <Link to="/register" data-testid="landing-register-link" className="font-mono text-xs uppercase tracking-wider bg-acid text-ink font-bold px-4 py-2.5 hard-shadow">
            Start Collecting
          </Link>
        </div>
      </header>

      <div className="relative z-20 px-6 lg:px-12 pt-4 bg-background">
        <AdBanner />
      </div>

      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="A pile of concert, movie and vintage posters" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
        </div>

        <div className="relative z-10 px-6 lg:px-12 py-16 max-w-3xl">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-acid mb-5">Your Favorite Poster Portfolios</div>
          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl text-paper leading-[0.85] ink-offset">
            EVERY POSTER<br />WORTH<br />COLLECTING
          </h1>
          <p className="mt-6 font-sans text-base text-paper/80 max-w-lg leading-relaxed">
            One vault for your entire poster archive — gig prints, movie one-sheets, theater playbills,
            vintage ads and travel art. Track print numbers, foil & glow variants, framer notes, condition
            and market value. Map your whole collecting history, one poster at a time.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <span key={c} className="font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 border border-stroke bg-ink/60 backdrop-blur-sm text-paper">
                {c}
              </span>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/register" data-testid="hero-cta-button" className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-wider bg-acid text-ink font-bold px-7 py-4 hard-shadow">
              Build Your Vault <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <section className="relative z-10 px-6 lg:px-12 py-16 border-t border-stroke grid sm:grid-cols-3 gap-6 bg-background">
        {[
          { icon: Layers, title: "Detailed Catalog", desc: "Category, dimensions, print run, variant, condition, framer & valuation on every piece." },
          { icon: MapPin, title: "Collection Map", desc: "Glowing pins trace every venue, cinema and city your posters came from." },
          { icon: Clock, title: "History Timeline", desc: "A chronological ink-line of shows and releases built from your collection." },
        ].map((f) => (
          <div key={f.title} className="bg-surface border border-stroke p-6 hard-shadow">
            <f.icon className="text-acid mb-4" size={28} />
            <h3 className="font-display text-2xl text-paper">{f.title}</h3>
            <p className="font-sans text-sm text-muted-gray mt-2 leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </section>

      <Footer />
    </div>
  );
}
